import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { DOC_TYPES, ORDER_STATUSES } from '../firebase/schema';

const GOALS_DOC_REF = ['dashboardSettings', 'monthlySalesGoals'];

const getMonthKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const toAmount = (order) => {
  const total = Number(
    order?.orderTotal ??
    order?.total ??
    order?.subtotal ??
    order?.totalAmount ??
    0
  );
  return Number.isFinite(total) ? total : 0;
};

const getAmountInfo = (order) => {
  const candidates = [
    ['orderTotal', order?.orderTotal],
    ['total', order?.total],
    ['subtotal', order?.subtotal],
    ['totalAmount', order?.totalAmount]
  ];

  const firstPresent = candidates.find(([, value]) => value != null && value !== '');
  const baseAmount = Number(firstPresent?.[1] ?? 0);
  const salesTax = Number(order?.salesTax || 0);
  const amount =
    firstPresent?.[0] === 'orderTotal' && Number.isFinite(salesTax)
      ? baseAmount + salesTax
      : baseAmount;
  return {
    amount: Number.isFinite(amount) ? amount : 0,
    source:
      firstPresent?.[0] === 'orderTotal' && salesTax > 0
        ? 'orderTotal+salesTax'
        : firstPresent?.[0] || 'none'
  };
};

const sumOrderLinesAmount = (lineDocs) => {
  let totalFromLinePrice = 0;
  let hasLinePrice = false;
  let totalFromQtyPrice = 0;
  let hasQtyPrice = false;

  lineDocs.forEach((lineDoc) => {
    const line = lineDoc.data();
    const totalLinePrice = Number(line?.totalLinePrice);
    if (Number.isFinite(totalLinePrice) && totalLinePrice > 0) {
      totalFromLinePrice += totalLinePrice;
      hasLinePrice = true;
    }

    const qty = Number(line?.totalQuantity);
    const pricePerPiece = Number(line?.pricePerPiece);
    if (Number.isFinite(qty) && Number.isFinite(pricePerPiece) && qty > 0 && pricePerPiece >= 0) {
      totalFromQtyPrice += qty * pricePerPiece;
      hasQtyPrice = true;
    }
  });

  if (hasLinePrice) {
    return { amount: totalFromLinePrice, source: 'orderLines.totalLinePrice' };
  }
  if (hasQtyPrice) {
    return { amount: totalFromQtyPrice, source: 'orderLines.qty*pricePerPiece' };
  }
  return { amount: 0, source: 'none' };
};

const chunkIds = (ids, size) => {
  const out = [];
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size));
  }
  return out;
};

/**
 * Sum line $ by orderId; batches `in` queries (max 10 per Firestore `in`).
 * Batches run concurrently so the fallback stays fast even with many orders.
 */
const fetchLineAmountByOrderId = async (db, orderIds) => {
  const unique = [...new Set(orderIds.filter(Boolean))];
  const result = new Map();
  unique.forEach((id) => result.set(id, { amount: 0, source: 'none' }));

  const batches = chunkIds(unique, 10).filter((batch) => batch.length > 0);
  await Promise.all(
    batches.map(async (batch) => {
      try {
        const snap = await getDocs(
          query(collection(db, 'orderLines'), where('orderId', 'in', batch))
        );
        const byOrder = new Map();
        snap.docs.forEach((d) => {
          const oid = d.data()?.orderId;
          if (!oid) return;
          if (!byOrder.has(oid)) byOrder.set(oid, []);
          byOrder.get(oid).push(d);
        });
        batch.forEach((oid) => {
          const docs = byOrder.get(oid) || [];
          result.set(oid, sumOrderLinesAmount(docs));
        });
      } catch (error) {
        console.error('Error batch-fetching orderLines for amounts:', error);
      }
    })
  );
  return result;
};

const resolveOrderAmounts = async (db, orders) => {
  const lineByOrder = await fetchLineAmountByOrderId(
    db,
    orders.map((o) => o.id)
  );
  const baseInfos = new Map();

  orders.forEach((order) => {
    const docInfo = getAmountInfo(order);
    const lineInfo = lineByOrder.get(order.id) || { amount: 0, source: 'none' };
    baseInfos.set(
      order.id,
      lineInfo.amount > docInfo.amount
        ? {
            amount: lineInfo.amount,
            source:
              docInfo.source === 'none'
                ? lineInfo.source
                : `${lineInfo.source} (lines > ${docInfo.source})`
          }
        : docInfo
    );
  });

  return baseInfos;
};

const isRealOrder = (order) => {
  if (!order) return false;
  if (order.type === DOC_TYPES.QUOTE) return false;
  if (String(order.status || '').toUpperCase() === 'QUOTE') return false;
  return true;
};

/** Firestore Timestamp, Date, millis, or ISO string → Date or null */
const toJsDate = (value) => {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const isDateInMonth = (value, startOfMonth, endOfMonth) => {
  const d = toJsDate(value);
  if (!d) return false;
  return d >= startOfMonth && d < endOfMonth;
};

/** MTD invoiced: real orders billed this month (invoicedAt), or archived/completed with completedAt this month (Invoicing flow). */
const isMtdInvoicedOrder = (order, startOfMonth, endOfMonth) => {
  if (!isRealOrder(order)) return false;
  if (order.status === ORDER_STATUSES.CANCELLED) return false;
  if (isDateInMonth(order.invoicedAt, startOfMonth, endOfMonth)) return true;
  const finalized =
    order.archived === true || order.status === ORDER_STATUSES.COMPLETED;
  if (finalized && isDateInMonth(order.completedAt, startOfMonth, endOfMonth)) {
    return true;
  }
  return false;
};

export const getMonthlySalesGoal = async (db, date = new Date()) => {
  const monthKey = getMonthKey(date);
  const goalDoc = await getDoc(doc(db, ...GOALS_DOC_REF));
  if (!goalDoc.exists()) {
    return { monthKey, goal: 0 };
  }

  const goalsByMonth = goalDoc.data()?.goalsByMonth || {};
  const goal = Number(goalsByMonth[monthKey] || 0);
  return { monthKey, goal: Number.isFinite(goal) ? goal : 0 };
};

export const saveMonthlySalesGoal = async (db, goal, date = new Date()) => {
  const monthKey = getMonthKey(date);
  const numericGoal = Math.max(0, Number(goal) || 0);

  await setDoc(
    doc(db, ...GOALS_DOC_REF),
    {
      goalsByMonth: {
        [monthKey]: numericGoal
      },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return { monthKey, goal: numericGoal };
};

export const getMonthlySalesGoalsForYear = async (db, year = new Date().getFullYear()) => {
  const goalDoc = await getDoc(doc(db, ...GOALS_DOC_REF));
  const goalsByMonth = goalDoc.exists() ? goalDoc.data()?.goalsByMonth || {} : {};
  const result = {};

  for (let month = 1; month <= 12; month += 1) {
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    const value = Number(goalsByMonth[monthKey] || 0);
    result[monthKey] = Number.isFinite(value) ? value : 0;
  }

  return result;
};

export const saveMonthlySalesGoalsForYear = async (db, goalsByMonth) => {
  const sanitized = Object.entries(goalsByMonth || {}).reduce((acc, [monthKey, value]) => {
    const numericValue = Math.max(0, Number(value) || 0);
    acc[monthKey] = numericValue;
    return acc;
  }, {});

  await setDoc(
    doc(db, ...GOALS_DOC_REF),
    {
      goalsByMonth: sanitized,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return sanitized;
};

export const getMonthlySalesProgress = async (db, date = new Date()) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

  const [goalData, allOrdersSnap] = await Promise.all([
    getMonthlySalesGoal(db, date),
    getDocs(collection(db, 'orders'))
  ]);

  const allOrders = allOrdersSnap.docs.map((orderDoc) => ({
    id: orderDoc.id,
    ...orderDoc.data()
  }));

  const invoicedOrders = allOrders.filter((order) =>
    isMtdInvoicedOrder(order, startOfMonth, endOfMonth)
  );

  const uninvoicedOrders = allOrders.filter((order) => {
    if (!isRealOrder(order)) return false;
    // WIP = active real orders that have not been invoiced yet.
    // Includes "Ready To Invoice", excludes archived/completed/cancelled.
    if (order.archived) return false;
    if ([ORDER_STATUSES.COMPLETED, ORDER_STATUSES.CANCELLED].includes(order.status)) return false;
    return !order.invoiced;
  });

  const ordersForAmounts = [
    ...new Map([...invoicedOrders, ...uninvoicedOrders].map((o) => [o.id, o])).values()
  ];
  const amountInfoMap = await resolveOrderAmounts(db, ordersForAmounts);

  const mtdInvoiced = invoicedOrders.reduce((sum, order) => {
    const info = amountInfoMap.get(order.id);
    return sum + Number(info?.amount || 0);
  }, 0);
  const notInvoicedTotal = uninvoicedOrders.reduce((sum, order) => {
    const info = amountInfoMap.get(order.id);
    return sum + Number(info?.amount || 0);
  }, 0);

  return {
    monthKey: goalData.monthKey,
    goal: goalData.goal,
    mtdInvoiced,
    notInvoicedTotal,
    allCurrent: mtdInvoiced + notInvoicedTotal
  };
};

export const getMonthlySalesWipAudit = async (db) => {
  const orders = (await getDocs(query(collection(db, 'orders')))).docs.map((orderDoc) => ({
    id: orderDoc.id,
    ...orderDoc.data()
  }));
  const amountInfoMap = await resolveOrderAmounts(db, orders);

  const rows = orders.map((order) => {
    const info = amountInfoMap.get(order.id) || { amount: 0, source: 'none' };

    let included = true;
    let reason = 'included';

    if (!isRealOrder(order)) {
      included = false;
      reason = 'quote';
    } else if (order.archived) {
      included = false;
      reason = 'archived=true';
    } else if ([ORDER_STATUSES.COMPLETED, ORDER_STATUSES.CANCELLED].includes(order.status)) {
      included = false;
      reason = `status=${order.status || 'unknown'}`;
    } else if (order.invoiced) {
      included = false;
      reason = 'invoiced=true';
    }

    return {
      id: order.id,
      poNumber: order.poNumber || '',
      customerName: order.customerName || order.client || '',
      status: order.status || '',
      archived: Boolean(order.archived),
      invoiced: Boolean(order.invoiced),
      amount: info.amount,
      amountSource: info.source,
      included,
      reason
    };
  });

  const includedTotal = rows
    .filter((row) => row.included)
    .reduce((sum, row) => sum + row.amount, 0);

  return { rows, includedTotal };
};
